<template>
  <span class="date-input-wrapper">
    <template v-if="formattedLabelText">
      <label
        :for="formattedHtmlId"
        class="sc3-form-label sc3-form-label-inline sc3-form-label-small"
      >{{ formattedLabelText }}</label>
      <br />
    </template>
    <input
      :id="formattedHtmlId"
      type="text"
      class="sc3-text-input sc3-text-input-inline sc3-text-input-compact sc3-text-input-grey-and-black"
      autocomplete="off"
      :placeholder="formattedPlaceholderText"
      ref="dateInputElement"
    />
  </span>
</template>

<script lang="ts" setup>
  import { ref, toRefs, computed, watch, onMounted, onUnmounted } from 'vue';
  import flatpickr from 'flatpickr';
  import { isSharpCharts2DateStringOlderThanMinimumAllowed } from '@/composables/isSharpCharts2DateStringOlderThanMinimumAllowed';
  import { getFormattedTodayDate } from '@/utils/getFormattedTodayDate';
  import { isValidSharpCharts2DateString } from '@/utils/isValidSharpCharts2DateString';
  import { formatSharpCharts2DateString } from '@/utils/formatSharpCharts2DateString';
  import { eraseHoursMinutesSecondsAndMilliseconds } from '@/utils/eraseHoursMinutesSecondsAndMilliseconds';
  import { MIN_ALLOWED_DATE_STRING } from '@/enums/min-allowed-date-string';

  type FlatpickrOptions = flatpickr.Options.Options;
  type FlatpickrInstance = flatpickr.Instance;

  const dateInputElement = ref<HTMLInputElement>();

  const props = defineProps<{
    dateString: string;
    labelText: string;
    placeholderText: string;
    htmlId: string;
  }>();
  const {
    dateString,
    labelText,
    placeholderText,
    htmlId
  } = toRefs(props);

  const emit = defineEmits<{
    (e: 'update:dateString', dateString: string): void;
  }>();

  const formattedLabelText = computed(() => labelText.value.trim().toUpperCase());
  const formattedPlaceholderText = computed(() => placeholderText.value.trim());
  const formattedHtmlId = computed(() => htmlId.value.trim().toLowerCase());
  let datePicker: FlatpickrInstance | null = null;
  const TODAY = '(today)';

  function formatDate(dateObj?: Date) {
    if (!dateObj) {
      return TODAY;
    }

    dateObj = eraseHoursMinutesSecondsAndMilliseconds(dateObj);
    const formattedDate = formatSharpCharts2DateString(dateObj);

    if (formattedDate === getFormattedTodayDate()) {
      return TODAY;
    }

    return formattedDate;
  }

  function handleDatePickerClosed() {
    if (
      !datePicker ||
      datePicker.selectedDates.length !== 1 ||
      !datePicker.selectedDates[0]
    ) {
      return;
    }

    let newDateString = formatDate(datePicker.selectedDates[0]);

    if (newDateString === TODAY) {
      newDateString = '';
    }

    if (
      !isValidSharpCharts2DateString(newDateString) ||
      isSharpCharts2DateStringOlderThanMinimumAllowed(newDateString)
    ) {
      return;
    }

    emit('update:dateString', newDateString);
  }

  function handleDateStringChanged() {
    if (!datePicker) {
      return;
    }

    const newDateString = dateString.value ? dateString.value : getFormattedTodayDate();

    datePicker.setDate(newDateString, false);
  }

  watch(dateString, handleDateStringChanged);

  onMounted(() => {
    const flatpickrConfig: FlatpickrOptions = {
      defaultDate: dateString.value ? dateString.value : getFormattedTodayDate(),
      dateFormat: 'Y-m-d',
      allowInput: true,
      allowInvalidPreload: true,
      onClose: handleDatePickerClosed,
      formatDate: formatDate,
      minDate: MIN_ALLOWED_DATE_STRING
    };

    datePicker = flatpickr(dateInputElement.value!, flatpickrConfig);

    onUnmounted(() => {
      datePicker?.destroy();
      datePicker = null;
    })
  });
</script>

<style scoped>
  .date-input-wrapper {
    display: inline-block;
  }

  .sc3-text-input.sc3-text-input-inline {
    max-width: 88px;
  }

  .flatpickr-calendar,
  .dayContainer,
  .flatpickr-weekdaycontainer {
    width: 230px;
    min-width: 230px;
    max-width: 230px;
  }

  .flatpickr-months {
    height: 25px;
  }

  .flatpickr-months .flatpickr-prev-month,
  .flatpickr-months .flatpickr-next-month {
    padding-top: 4px;
  }

  .flatpickr-months .flatpickr-next-month {
    padding-left: 3px;
  }

  .flatpickr-months .flatpickr-month .flatpickr-current-month {
    left: 0;
    width: 100%;
    padding-top: 5px;
  }

  .flatpickr-months .flatpickr-month .flatpickr-current-month .flatpickr-monthDropdown-months {
    font-size: 15px;
  }

  .flatpickr-months .flatpickr-month .flatpickr-current-month .numInputWrapper {
    left: 8px;
    width: 58px;
  }

  .flatpickr-months .flatpickr-month .flatpickr-current-month .numInputWrapper .numInput {
    font-size: 16px;
  }

  .flatpickr-weekdaycontainer {
    font-size: 11px;
  }

  .flatpickr-day {
    height: 28px;
    line-height: 28px;
  }
</style>
